import React, {useState} from 'react';

import find from 'lodash/find';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {AttachOverlay, AttachOverlayChildProps} from 'app/general/overlays';
import {CheckboxButton} from 'app/general/widgets/CheckboxButton';
import {SimpleItemPickerListPopoverRTK} from 'app/general/widgets/SimpleItemPickerList';
import {useTeams} from 'app/teams/api';
import {useInviteUser} from 'app/user/api';
import {gt} from 'app/utils/i18n';

import {convertLocationParamsToFetchParams} from '../../../teams/utils';

import type {NewInvitationType} from 'app/user/types';
import type {TeamSerializedType, UserSerializedType} from 'sweet-types';

interface Props {
  user: UserSerializedType;
  refreshList: () => void;
}

export function UserDetailPageAddToTeamButton({
  user,
  refreshList,
}: Props): JSX.Element {
  const [giveManagerAccess, setGiveManagerAccess] = useState(false);
  const [inviteUser] = useInviteUser();

  function addUserToTeams(teams: Array<TeamSerializedType>) {
    const invitations = teams.map<NewInvitationType>((team) => ({
      send_mail: false,
      content_type: 'team',
      permission: giveManagerAccess ? 'edit' : 'view',
      object_id: team.id,
      to_user_id: user.url,
    }));
    return inviteUser({invitations}).then(refreshList); // We need refreshList for now, will replace it asap.
  }

  function getTeamItemProps(team: TeamSerializedType) {
    return {
      disabledText:
        find(user.teams, {
          id: team.id,
        }) != null
          ? gt('Already team member')
          : undefined,
    };
  }

  return user.is_guest_until == null ? (
    <AttachOverlay
      trigger="click"
      overlay={({overlayProps}) => (
        <SimpleItemPickerListPopoverRTK
          showDescription={false}
          overlayProps={{...overlayProps, placement: 'left'}}
          additionalParams={{
            exclude_user_id: user.id,
          }}
          paramsConverter={convertLocationParamsToFetchParams}
          useDataHook={useTeams}
          addItemProps={getTeamItemProps}
          onConfirm={addUserToTeams}
          searchPlaceholder={gt('Search all teams')}
          isPopover={true}
          isSmall={true}
        >
          {(selectedItems) =>
            selectedItems.length > 0 ? (
              <div className="actions-block">
                <CheckboxButton
                  className="btn btn-sm btn-link"
                  isChecked={giveManagerAccess}
                  onClick={() => setGiveManagerAccess(!giveManagerAccess)}
                >
                  {gt('Give manager access')}
                </CheckboxButton>
              </div>
            ) : null
          }
        </SimpleItemPickerListPopoverRTK>
      )}
    >
      {(triggers) => <Button {...triggers} />}
    </AttachOverlay>
  ) : (
    <LazyTooltip
      tooltip={gt('Guest member cannot be a part of a team')}
      placement="bottom-end"
    >
      <div>
        <Button disabled={true} />
      </div>
    </LazyTooltip>
  );
}

interface ButtonProps extends Partial<AttachOverlayChildProps> {
  disabled?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (props: ButtonProps, ref): JSX.Element => (
    <button className="btn btn-light btn-sm" {...props} ref={ref}>
      <i className="fa fa-users" />{' '}
      <span className="d-none d-md-inline">{gt('Add to team')}</span>
    </button>
  )
);
